import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { apiClient } from '@/lib/api/client'
import type { OpsSystem } from '@/lib/api/types'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/ops/system')({
  // Superuser gating lives on the /ops layout route (ops.tsx); the endpoint
  // enforces the real boundary server-side.
  component: SystemPage,
})

const REFRESH_MS = 60_000
const PRESETS = ['1h', '6h', '24h', '7d'] as const
/** Cloud Monitoring retains roughly six weeks; the picker refuses older so the
 *  user gets a message instead of a silently empty chart. */
const MAX_LOOKBACK_DAYS = 42

type Sample = { timestamp: string; value: number }

function num(value: number | null | undefined, digits = 2): string {
  if (value == null) return '—'
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

function clockLabel(iso: string, spanMs: number): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  // Over a day, the time alone is ambiguous — show the date too.
  return spanMs > 36 * 3600 * 1000
    ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/** `datetime-local` needs a naive local string; toISOString() would shift it. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function headroom(used: number | null | undefined, provisioned: number | null | undefined) {
  if (used == null || !provisioned) return null
  return used / provisioned
}

function toneFor(fraction: number | null): string {
  if (fraction == null) return 'text-neutral-500 dark:text-neutral-400'
  if (fraction >= 0.8) return 'text-red-600 dark:text-red-400'
  if (fraction >= 0.6) return 'text-amber-600 dark:text-amber-400'
  return 'text-green-700 dark:text-green-400'
}

/** Sparkline with a hover readout. Dependency-free: a charting library is not
 *  worth the bundle for six single-series charts. */
function Chart({
  series,
  format,
  spanMs,
}: {
  series: Sample[]
  format: (v: number) => string
  spanMs: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<number | null>(null)

  const geometry = useMemo(() => {
    if (series.length < 2) return null
    const values = series.map((s) => s.value)
    const max = Math.max(...values)
    const min = Math.min(...values)
    const span = max - min || 1
    const step = 100 / (series.length - 1)
    const xy = series.map((s, i) => ({
      x: i * step,
      y: 28 - ((s.value - min) / span) * 26,
    }))
    return { xy, d: xy.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ') }
  }, [series])

  if (!geometry) {
    return <div className="h-10 flex items-end text-xs text-neutral-400">not enough data</div>
  }

  const point = hover == null ? null : series[hover]

  return (
    <div
      ref={ref}
      className="relative"
      onMouseLeave={() => setHover(null)}
      onMouseMove={(e) => {
        const rect = ref.current?.getBoundingClientRect()
        if (!rect || rect.width === 0) return
        const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
        setHover(Math.round(fraction * (series.length - 1)))
      }}
    >
      <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="h-10 w-full">
        <path
          d={geometry.d}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
        {hover != null && (
          <>
            <line
              x1={geometry.xy[hover].x}
              y1={0}
              x2={geometry.xy[hover].x}
              y2={30}
              stroke="currentColor"
              strokeWidth="1"
              opacity="0.35"
              vectorEffect="non-scaling-stroke"
            />
            {/* Non-scaling radius: the viewBox is stretched horizontally, so a
                plain <circle r> renders as an ellipse. */}
            <circle
              cx={geometry.xy[hover].x}
              cy={geometry.xy[hover].y}
              r="1.5"
              fill="currentColor"
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
      </svg>
      {point && (
        <div
          className={cn(
            'pointer-events-none absolute -top-1 z-10 whitespace-nowrap rounded border',
            'border-neutral-200 bg-white px-2 py-1 text-xs shadow-sm',
            'dark:border-neutral-700 dark:bg-neutral-900',
            // Flip the tooltip past the midpoint so it never leaves the card.
            hover! > series.length / 2 ? 'right-0' : 'left-0',
          )}
        >
          <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">
            {format(point.value)}
          </span>
          <span className="ml-2 text-neutral-500 dark:text-neutral-400">
            {clockLabel(point.timestamp, spanMs)}
          </span>
        </div>
      )}
    </div>
  )
}

function Panel({
  title,
  value,
  sub,
  series,
  format,
  spanMs,
  tone,
  error,
  tabs,
}: {
  title: string
  value: string
  sub?: string
  series?: Sample[]
  format?: (v: number) => string
  spanMs: number
  tone?: string
  error?: string
  tabs?: { options: string[]; active: string; onSelect: (v: string) => void }
}) {
  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {title}
        </div>
        {tabs && (
          <div className="flex shrink-0 overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-800">
            {tabs.options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => tabs.onSelect(option)}
                className={cn(
                  'px-1.5 py-0.5 text-[11px] leading-4',
                  option === tabs.active
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                    : 'text-neutral-500 dark:text-neutral-400',
                )}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
      {error ? (
        <div className="mt-2 text-sm text-amber-600 dark:text-amber-400">unavailable — {error}</div>
      ) : (
        <>
          <div className={cn('mt-1 text-2xl font-semibold tabular-nums', tone)}>{value}</div>
          {sub && <div className="text-xs text-neutral-500 dark:text-neutral-400">{sub}</div>}
          {series && (
            <div className="mt-2 text-neutral-400 dark:text-neutral-600">
              <Chart series={series} format={format ?? ((v) => num(v))} spanMs={spanMs} />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SystemPage() {
  const { token } = useAuth()
  const [preset, setPreset] = useState<string>('6h')
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null)
  const [draft, setDraft] = useState(() => {
    const now = new Date()
    const then = new Date(now.getTime() - 6 * 3600 * 1000)
    return { start: toLocalInput(then), end: toLocalInput(now) }
  })
  const [live, setLive] = useState(true)
  // p95 by default: on a low-traffic service p99 is largely a single slow
  // request, while p50 hides the tail that actually costs guests.
  const [latencyPercentile, setLatencyPercentile] = useState<'p50' | 'p95' | 'p99'>('p95')
  const [now, setNow] = useState(() => Date.now())
  const [rangeError, setRangeError] = useState<string | null>(null)

  // A fixed historical range never changes, so polling it is pure waste.
  const isLive = live && !custom
  const range = custom
    ? { start: new Date(custom.start).toISOString(), end: new Date(custom.end).toISOString() }
    : { window: preset }

  const { data, isLoading, isFetching, error, dataUpdatedAt, refetch } = useQuery<OpsSystem>({
    queryKey: ['ops-system', custom?.start ?? preset, custom?.end ?? ''],
    queryFn: () => apiClient.getOpsSystem(token ?? '', range),
    enabled: !!token,
    refetchInterval: isLive ? REFRESH_MS : false,
    refetchOnWindowFocus: isLive,
    placeholderData: (previous) => previous,
  })

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  function applyCustom() {
    const start = new Date(draft.start)
    const end = new Date(draft.end)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setRangeError('Enter both a start and an end.')
      return
    }
    if (end <= start) {
      setRangeError('End must be after start.')
      return
    }
    const oldest = Date.now() - MAX_LOOKBACK_DAYS * 86400_000
    if (start.getTime() < oldest) {
      setRangeError(`Monitoring only retains about ${MAX_LOOKBACK_DAYS} days.`)
      return
    }
    setRangeError(null)
    setCustom({ ...draft })
    // Polling a fixed range would re-fetch identical data forever.
    setLive(false)
  }

  const errs = data?.panel_errors ?? {}
  const s = data?.summary
  const cpuFraction = headroom(s?.sql_cores_used_now, s?.sql_cores_provisioned)
  const memFraction = headroom(s?.sql_memory_used_gib, s?.sql_memory_quota_gib)
  const age = dataUpdatedAt ? Math.max(0, Math.round((now - dataUpdatedAt) / 1000)) : null
  const spanMs =
    data?.start && data?.end
      ? new Date(data.end).getTime() - new Date(data.start).getTime()
      : 0

  const latencySeries = {
    p50: data?.run_latency_p50,
    p95: data?.run_latency_p95,
    p99: data?.run_latency_p99,
  }[latencyPercentile]

  // The headline is the percentile over the WHOLE range; the sparkline keeps
  // the per-bucket trend. Falls back to the latest bucket only if that panel
  // failed, which is the old, much noisier behaviour.
  const latencyWindowMs = {
    p50: data?.run_latency_window_p50,
    p95: data?.run_latency_window_p95,
    p99: data?.run_latency_window_p99,
  }[latencyPercentile]
  const latencyHeadlineMs =
    typeof latencyWindowMs === 'number' ? latencyWindowMs : latencySeries?.at(-1)?.ms
  const latencyIsWindow = typeof latencyWindowMs === 'number'

  const alignSeconds = data?.alignment_seconds
  const bucketLabel = alignSeconds
    ? alignSeconds % 3600 === 0
      ? `${alignSeconds / 3600}h`
      : alignSeconds % 60 === 0
        ? `${alignSeconds / 60}-min`
        : `${alignSeconds}s`
    : null
  const windowLabel =
    data?.window && data.window !== 'custom'
      ? data.window
      : spanMs
        ? spanMs >= 86400000
          ? `${Math.round(spanMs / 86400000)}d`
          : `${Math.max(1, Math.round(spanMs / 3600000))}h`
        : null
  const latencySub = latencyIsWindow
    ? [windowLabel && `over the last ${windowLabel}`,
       bucketLabel && `charted in ${bucketLabel} buckets`]
        .filter(Boolean)
        .join(' · ') || 'over the selected range'
    : bucketLabel
      ? `latest ${bucketLabel} bucket · range total unavailable`
      : 'latest bucket · range total unavailable'

  const samples = <
    T extends { timestamp: string },
    K extends Exclude<keyof T, 'timestamp'>
  >(
    rows: T[] | undefined,
    key: K,
  ): Sample[] => (rows ?? []).map((r) => ({ timestamp: r.timestamp, value: Number(r[key]) }))

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <h1 className="text-xl font-semibold">System</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Cloud SQL and Cloud Run health. Read-only.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => {
                setPreset(w)
                setCustom(null)
                setRangeError(null)
                setLive(true)
              }}
              className={cn(
                'px-2.5 py-1 text-sm rounded-md border',
                !custom && preset === w
                  ? 'bg-neutral-900 text-white border-neutral-900 dark:bg-white dark:text-neutral-900'
                  : 'border-neutral-200 dark:border-neutral-800',
              )}
            >
              {w}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCustom((c) => (c ? null : { ...draft }))}
            className={cn(
              'px-2.5 py-1 text-sm rounded-md border',
              custom
                ? 'bg-neutral-900 text-white border-neutral-900 dark:bg-white dark:text-neutral-900'
                : 'border-neutral-200 dark:border-neutral-800',
            )}
          >
            Custom
          </button>
          <label
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-md border',
              custom
                ? 'opacity-40 cursor-not-allowed border-neutral-200 dark:border-neutral-800'
                : 'border-neutral-200 dark:border-neutral-800 cursor-pointer',
            )}
            title={custom ? 'A fixed range does not change — nothing to poll.' : undefined}
          >
            <input
              type="checkbox"
              className="accent-green-600"
              checked={isLive}
              disabled={!!custom}
              onChange={(e) => setLive(e.target.checked)}
            />
            <span className="flex items-center gap-1">
              {isLive && <span className="inline-block h-2 w-2 rounded-full bg-green-500" />}
              Live
            </span>
          </label>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-2.5 py-1 text-sm rounded-md border border-neutral-200 dark:border-neutral-800"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {custom && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-neutral-200 dark:border-neutral-800 p-3">
          <label className="text-xs text-neutral-500 dark:text-neutral-400">
            From
            <input
              type="datetime-local"
              value={draft.start}
              onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
              className="mt-1 block rounded-md border border-neutral-200 dark:border-neutral-800 bg-transparent px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-neutral-500 dark:text-neutral-400">
            To
            <input
              type="datetime-local"
              value={draft.end}
              onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
              className="mt-1 block rounded-md border border-neutral-200 dark:border-neutral-800 bg-transparent px-2 py-1 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={applyCustom}
            className="px-3 py-1.5 text-sm rounded-md bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
          >
            Apply
          </button>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            local time · about {MAX_LOOKBACK_DAYS} days of history available
          </span>
        </div>
      )}

      {rangeError && (
        <div className="rounded-md border border-amber-200 dark:border-amber-900 p-3 text-sm text-amber-700 dark:text-amber-400">
          {rangeError}
        </div>
      )}

      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        {custom && data?.start && data?.end ? (
          <>
            showing {clockLabel(data.start, spanMs)} → {clockLabel(data.end, spanMs)} · fixed range,
            not refreshing
          </>
        ) : (
          age != null && <>updated {age}s ago · metrics lag GCP by 1–3 minutes</>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 dark:border-red-900 p-3 text-sm text-red-700 dark:text-red-400">
          {(error as Error).message}
        </div>
      )}

      {/* An unavailable feed must SAY so. Rendering zeroes here would read as
          "everything is healthy", which is the opposite of the truth. */}
      {data && !data.available && (
        <div className="rounded-md border border-amber-200 dark:border-amber-900 p-3 text-sm text-amber-700 dark:text-amber-400">
          Metrics unavailable{data.reason ? ` — ${data.reason}` : ''}.
        </div>
      )}

      {isLoading && !data && (
        <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading…</div>
      )}

      {data?.available && (
        <>
          <section>
            <h2 className="text-sm font-medium mb-2">Cloud SQL</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Panel
                title="CPU cores in use"
                value={num(s?.sql_cores_used_now)}
                // Cores AND the provisioned count: a percentage alone is
                // meaningless on an instance that gets resized between events.
                sub={`of ${num(s?.sql_cores_provisioned, 0)} provisioned · peak ${num(s?.sql_cores_used_peak)}`}
                tone={toneFor(cpuFraction)}
                series={samples(data.sql_cpu, 'cores_used')}
                format={(v) => `${num(v)} cores`}
                spanMs={spanMs}
                error={errs.sql_cpu}
              />
              <Panel
                title="Memory"
                value={`${num(s?.sql_memory_used_gib, 1)} GiB`}
                sub={`of ${num(s?.sql_memory_quota_gib, 1)} GiB`}
                tone={toneFor(memFraction)}
                series={samples(data.sql_memory, 'used_gib')}
                format={(v) => `${num(v, 1)} GiB`}
                spanMs={spanMs}
                error={errs.sql_memory}
              />
              <Panel
                title="Connections"
                value={num(data.sql_connections?.at(-1)?.backends, 0)}
                sub="backends across databases"
                series={samples(data.sql_connections, 'backends')}
                format={(v) => `${num(v, 0)} backends`}
                spanMs={spanMs}
                error={errs.sql_connections}
              />
              <Panel
                title="Provisioned"
                value={`${num(s?.sql_cores_provisioned, 0)} vCPU`}
                sub="changes here are manual resizes"
                series={samples(data.sql_cpu, 'cores_provisioned')}
                format={(v) => `${num(v, 0)} vCPU`}
                spanMs={spanMs}
                error={errs.sql_cpu}
              />
            </div>
          </section>

          <section>
            <h2 className="text-sm font-medium mb-2">Cloud Run</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Panel
                title="Active instances"
                value={num(s?.run_instances_now, 0)}
                sub={`peak ${num(s?.run_instances_peak, 0)} in window`}
                series={samples(data.run_instances, 'active')}
                format={(v) => `${num(v, 0)} instances`}
                spanMs={spanMs}
                error={errs.run_instances}
              />
              <Panel
                title="Request latency"
                value={`${num(latencyHeadlineMs, 0)} ms`}
                sub={latencySub}
                series={samples(latencySeries, 'ms')}
                format={(v) => `${num(v, 0)} ms`}
                spanMs={spanMs}
                tabs={{
                  options: ['p50', 'p95', 'p99'],
                  active: latencyPercentile,
                  onSelect: (v) => setLatencyPercentile(v as 'p50' | 'p95' | 'p99'),
                }}
                error={errs[`run_latency_${latencyPercentile}`]}
              />
              <Panel
                title="Requests"
                value={`${num(data.run_request_rate?.at(-1)?.per_second)}/s`}
                sub="all revisions"
                series={samples(data.run_request_rate, 'per_second')}
                format={(v) => `${num(v)}/s`}
                spanMs={spanMs}
                error={errs.run_request_rate}
              />
            </div>
          </section>
        </>
      )}
    </div>
  )
}
